import { Box } from '@mui/material';
import LelyButton from '../muiWrappers/LelyButton/LelyButton';
import type { LelyIconName } from '../muiWrappers/buttonTypes';

export type LelySwitchOption = {
  value: string;
  label?: string;
  ariaLabel?: string;
  leadingIcon?: LelyIconName;
  trailingIcon?: LelyIconName;
};

type Props = {
  options: [LelySwitchOption, LelySwitchOption];
  value: string;
  dense?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
  onChange: (value: string) => void;
};

function LelySwitch({
  options,
  value,
  dense = false,
  disabled = false,
  'data-testid': dataTestId,
  onChange,
}: Props) {
  const handleOptionClick = (nextValue: string) => {
    if (disabled || nextValue === value) {
      return;
    }

    onChange(nextValue);
  };

  return (
    <Box
      role="group"
      data-disabled={disabled || undefined}
      data-testid={dataTestId}
      sx={(theme) => ({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'visible',
        borderRadius: theme.lelyBorderRadius.SM,
        boxShadow: `0 0 0 1px ${theme.palette.border.default}`,
        background: theme.palette.background.secondaryDefault,
        opacity: disabled ? theme.palette.action.disabledOpacity : 1,
      })}
    >
      {options.map((option, index) => {
        const isSelected = option.value === value;
        const hasDivider = index < options.length - 1;
        const isNextSelected = options[index + 1]?.value === value;

        return (
          <LelyButton
            key={option.value}
            variant={isSelected ? 'primary' : 'secondary'}
            dense={dense}
            disabled={disabled}
            aria-pressed={isSelected}
            aria-label={option.ariaLabel ?? option.label ?? option.value}
            leadingIcon={option.leadingIcon}
            trailingIcon={option.trailingIcon}
            data-testid={
              dataTestId ? `${dataTestId}-${option.value}` : undefined
            }
            sx={(theme) => {
              const boxShadow =
                hasDivider && !isSelected && !isNextSelected
                  ? `inset -1px 0 0 ${theme.palette.border.default}`
                  : 'none';

              return {
                borderRadius: 0,
                boxShadow,
                '&.Mui-focusVisible': {
                  outline: `2px solid ${theme.palette.border.focus}`,
                  outlineOffset: theme.lelySpacing._2XS,
                  boxShadow,
                  zIndex: 1,
                },
                '&:hover, &:active': {
                  boxShadow,
                },
                ...(index === 0 && {
                  borderTopLeftRadius: theme.lelyBorderRadius.SM,
                  borderBottomLeftRadius: theme.lelyBorderRadius.SM,
                }),
                ...(index === options.length - 1 && {
                  borderTopRightRadius: theme.lelyBorderRadius.SM,
                  borderBottomRightRadius: theme.lelyBorderRadius.SM,
                }),
                ...(isSelected &&
                  index === 0 && {
                    borderTopRightRadius: theme.lelyBorderRadius.SM,
                    borderBottomRightRadius: theme.lelyBorderRadius.SM,
                  }),
                ...(isSelected &&
                  index === options.length - 1 && {
                    borderTopLeftRadius: theme.lelyBorderRadius.SM,
                    borderBottomLeftRadius: theme.lelyBorderRadius.SM,
                  }),
              };
            }}
            onClick={() => handleOptionClick(option.value)}
          >
            {option.label}
          </LelyButton>
        );
      })}
    </Box>
  );
}

export default LelySwitch;
